//@flow
import React, {
  useCallback,
  useMemo,
  useState,
  type StatelessFunctionalComponent,
} from "react";
import {
  type GridListColumnResultDTO,
  type SortDirectionEnum,
  GridListConstant,
} from "@hs/types";
import Icons from "@hs/icons";
import loc from "i18next";
import styles from "./CellHeader.module.css";
import Draggable from "react-draggable";

type Props = {
  col: GridListColumnResultDTO,
  columnIndex: number,
  style: StyleSheet,
  canColumnFilter: (name: string) => boolean,
  isColumnFiltered: (name: string) => boolean,
  isColumnSorted: (name: string) => boolean,
  getColumnSortDirection: (name: string) => SortDirectionEnum,
  parseCellHeaderCaption: (caption: string) => string,
  onHeaderClick: (columnIndex: number, header: string) => void,
  onHeaderFilterClick: (e: SyntheticMouseEvent<*>, name: string) => void,
  headerFilterTitle: ?string,
  onResize: (columnIndex: number, widthChange: number) => void,
  onMove: (columnIndex: number, positionChange: number) => void,
  onMoveEnd: (columnIndex: number, positionChange: number) => void,
  dropTargetProps: {
    isDropTarget: boolean,
    position: "Before" | "After",
  },
};

const CellHeader: StatelessFunctionalComponent<Props> = ({
  col,
  columnIndex,
  style,
  canColumnFilter,
  isColumnFiltered,
  isColumnSorted,
  getColumnSortDirection,
  onHeaderClick,
  onHeaderFilterClick,
  parseCellHeaderCaption,
  headerFilterTitle,
  onResize,
  onMove,
  onMoveEnd,
  dropTargetProps,
}: Props) => {
  const { caption: header, name } = col;
  const [key, setKey] = useState(`${name}`);
  const handleHeaderClick = useCallback(
    (e) => onHeaderClick(columnIndex, header),
    [columnIndex, header, onHeaderClick]
  );
  const handleHeaderFilterClick = useCallback(
    (e) => onHeaderFilterClick(e, name),
    [name, onHeaderFilterClick]
  );
  const isFiltered = isColumnFiltered(name);
  const colStyle = useMemo(
    () => (isFiltered ? { opacity: 1 } : { opacity: 0.2 }),
    [name, isFiltered]
  );

  // hack to force rerender the component
  const forceRender = useCallback(() => {
    setKey(`${name}-${Date.now()}`);
  }, [name]);

  const handleResize = useCallback(
    (widhtChange) => {
      onResize(columnIndex, widhtChange);
      forceRender();
    },
    [onResize, forceRender]
  );

  const handleMove = useCallback((columnIndex, positionChange) => {
    onMove(columnIndex, positionChange);
  }, []);

  const handleMoveEnd = useCallback(
    (columnIndex, positionChange) => {
      onMoveEnd(columnIndex, positionChange);
      forceRender();
    },
    [columnIndex, onMove, forceRender]
  );

  const caption = parseCellHeaderCaption(header);

  // Shrink the size of the drag handler to make room for the sort and filter icons if they are present
  const getDragHandlerShrink = useCallback(() => {
    let shrink = 0;
    if (col.canSort) {
      shrink += 15;
    }
    if (canColumnFilter(col.name)) {
      shrink += 15;
    }

    return shrink;
  }, [col.canSort, canColumnFilter, col.name]);

  /* istanbul ignore next */
  const filterTitle = useMemo(() => {
    return headerFilterTitle
      ? headerFilterTitle
      : loc.t("document:filterBy", {
          name: caption,
        });
  }, [headerFilterTitle, caption]);

  return (
    <div
      className={styles.header}
      style={style}
      title={caption}
      key={key}
      onClick={handleHeaderClick}
    >
      {col.canSort && (
        <Icons.Sort
          pullRight
          isSorted={isColumnSorted(col.name)}
          sortDirection={getColumnSortDirection(col.name)}
          name={caption}
        />
      )}
      {col.canSort && canColumnFilter(col.name) && (
        <Icons.Library
          title={filterTitle}
          name="fa-solid fa-filter"
          onClick={handleHeaderFilterClick}
          className={styles.filter}
          style={colStyle}
        />
      )}
      {dropTargetProps.isDropTarget && (
        <div
          className={`${styles.moveHighlight} ${
            styles[`moveHighlight${dropTargetProps.position}`]
          }`}
        />
      )}
      <Draggable
        axis="x"
        defaultClassName={styles.ReorderHandle}
        defaultClassNameDragging={styles.ReorderHandleActive}
        onStop={(_, a) => handleMoveEnd(columnIndex, a.x)}
        onDrag={(_, a) => handleMove(columnIndex, a.x)}
        grid={[10, 10]}
      >
        <div style={{ width: `calc(100% - ${getDragHandlerShrink()}px)` }}>
          <div className={styles.captionWrapper}>{caption}</div>
        </div>
      </Draggable>

      {/* ExtensionIcon and IconItemStatus column shouldn't be resizeable */}
      {name !== GridListConstant.ExtensionIcon && name !== GridListConstant.IconItemStatus && (
        <Draggable
          axis="x"
          defaultClassName={styles.DragHandle}
          defaultClassNameDragging={styles.DragHandleActive}
          onStop={(_, a) => handleResize(a.x)}
        >
          <div className={styles.DragHandleIcon} />
        </Draggable>
      )}
    </div>
  );
};
CellHeader.displayName = "GridList.CellHeader";

export default CellHeader;
